import { useQuery } from '@tanstack/react-query';
import { PageHeader } from '../components/PageHeader';
import { unwrap, useApi } from '../lib/api';
import { pages } from '../nav';

function errorMessage(err: unknown): string {
  // fetch() rejects with a TypeError ("Failed to fetch") when the API is not running.
  if (err instanceof TypeError) return 'Could not reach the API.';
  return err instanceof Error ? err.message : String(err);
}

export function DashboardPage() {
  const { client, baseUrl } = useApi();
  const version = useQuery({
    queryKey: ['version'],
    queryFn: async () => unwrap(await client.GET('/version')),
  });
  const stats = useQuery({
    queryKey: ['admin-stats'],
    queryFn: async () => unwrap(await client.GET('/admin/stats')),
  });

  // Test accounts (players.is_test_account) are left out of every count but "Test accounts" (admin tools, 2026-10-08).
  const tiles: [string, number | undefined, string][] = [
    [
      'Alive survivors',
      stats.data?.aliveSurvivors,
      'Real players whose active character is alive (zombies excluded).',
    ],
    [
      'Active (7 d)',
      stats.data?.active7d,
      'Real players who signed in, refreshed a session, sent a location or started a raid in the last 7 days.',
    ],
    ['Raids (24 h)', stats.data?.raidsLast24h, "Real players' raids started in the last 24 hours."],
    [
      'Rejected raids (24 h)',
      stats.data?.rejectedRaidsLast24h,
      "Real players' raid results the server rejected in the last 24 hours.",
    ],
    [
      'Flagged players',
      stats.data?.flaggedPlayers,
      'Real players with a trust score of 5 or more.',
    ],
    [
      'Test accounts',
      stats.data?.testAccounts,
      'Accounts marked as test accounts (scripts, dev builds), hidden from the player list by default.',
    ],
    ['Buildings', stats.data?.buildings, 'Imported and synthetic buildings on the map.'],
    ['Content version', stats.data?.contentVersion, 'The published content bundle.'],
  ];

  return (
    <>
      <PageHeader title={pages.dashboard.title} description={pages.dashboard.description}>
        <button
          type="button"
          onClick={() => {
            void version.refetch();
            void stats.refetch();
          }}
          className="rounded border-2 border-ink bg-white px-3 py-1.5 text-sm font-semibold hover:bg-caption/40"
        >
          Refresh
        </button>
      </PageHeader>

      <section
        aria-labelledby="api-status"
        className="mb-4 rounded border-2 border-ink bg-white p-4"
      >
        <h2 id="api-status" className="text-sm font-semibold tracking-wide text-bruise uppercase">
          API
        </h2>
        {version.isPending ? (
          <p className="mt-2">Checking&hellip;</p>
        ) : version.isError ? (
          <div role="status" className="mt-2">
            <p className="flex items-center gap-2 text-lg font-semibold">
              <span aria-hidden="true" className="inline-block size-3 rounded-full bg-blam" />
              API unreachable
            </p>
            <p className="mt-1 text-sm text-bruise">{errorMessage(version.error)}</p>
          </div>
        ) : (
          <p role="status" className="mt-2 flex items-center gap-2 text-lg font-semibold">
            <span aria-hidden="true" className="inline-block size-3 rounded-full bg-benday" />
            API {version.data.version} ({version.data.commit})
          </p>
        )}
        <p className="mt-3 font-mono text-xs text-bruise">{baseUrl}</p>
      </section>

      <div className="grid grid-cols-2 gap-4 md:grid-cols-4">
        {tiles.map(([label, value, caption]) => (
          <section key={label} title={caption} className="rounded border-2 border-ink bg-white p-4">
            <h2 className="text-xs font-semibold tracking-wide text-bruise uppercase">{label}</h2>
            <p className="mt-1 font-mono text-3xl">{value ?? '—'}</p>
            <p className="mt-1 text-xs text-bruise">{caption}</p>
          </section>
        ))}
      </div>

      <TriggerStats />
    </>
  );
}

/** Scan triggers over the last 7 days (ADR 0029 §17): scans, ready previews, refusals and redeems per trigger. */
function TriggerStats() {
  const { client } = useApi();
  const stats = useQuery({
    queryKey: ['admin-trigger-stats'],
    queryFn: async () =>
      unwrap(await client.GET('/admin/trigger-stats', { params: { query: { days: 7 } } })),
  });
  const rows = (stats.data?.triggers ?? [])
    .filter((t) => t.scans > 0 || t.redemptions > 0)
    .sort((a, b) => b.scans - a.scans || b.redemptions - a.redemptions);
  return (
    <section
      aria-labelledby="trigger-stats"
      className="mt-4 rounded border-2 border-ink bg-white p-4"
    >
      <h2 id="trigger-stats" className="text-sm font-semibold tracking-wide text-bruise uppercase">
        Scan triggers ({stats.data?.days ?? 7} days)
      </h2>
      {stats.isPending ? (
        <p className="mt-2">Loading&hellip;</p>
      ) : stats.isError ? (
        <p className="mt-2 text-sm text-blam">{errorMessage(stats.error)}</p>
      ) : rows.length === 0 ? (
        <p className="mt-2 text-sm text-bruise">No scans or redeems yet.</p>
      ) : (
        <table className="mt-2 w-full text-sm">
          <thead>
            <tr className="text-left text-xs text-bruise uppercase">
              <th className="py-1">Trigger</th>
              <th className="py-1">Action</th>
              <th className="py-1 text-right">Scans</th>
              <th className="py-1 text-right">Ready</th>
              <th className="py-1 text-right">Refused</th>
              <th className="py-1 text-right">Redeemed</th>
            </tr>
          </thead>
          <tbody>
            {rows.map((t) => (
              <tr
                key={t.triggerId}
                className="border-t border-newsprint"
                data-testid={`stat-${t.triggerId}`}
              >
                <td className="py-1">
                  {t.title || t.triggerId}{' '}
                  <span className="font-mono text-xs text-bruise">{t.triggerId}</span>
                </td>
                <td className="py-1 font-mono text-xs">{t.action ?? ''}</td>
                <td className="py-1 text-right font-mono">{t.scans}</td>
                <td className="py-1 text-right font-mono">{t.ready}</td>
                <td className="py-1 text-right font-mono">{t.refusals}</td>
                <td className="py-1 text-right font-mono">{t.redemptions}</td>
              </tr>
            ))}
          </tbody>
        </table>
      )}
    </section>
  );
}
